// Modo leitura (§4.2): a cópia do artigo guardada na ingestão (Markdown já sanitizado no servidor), renderizada aqui
// com marked + DOMPurify numa página limpa e serifada. Esc volta ao detalhe.
import DOMPurify from "dompurify";
import { marked } from "marked";
import { useEffect, useRef, useState } from "preact/hooks";
import type { Card } from "../../../../shared/types.ts";
import { MEDIA_SRC } from "../detail/extensions.ts";
import { openOver } from "../detail/Extras.tsx";
import { IconClose, IconExternal, IconSpin } from "../Icons.tsx";
import { store } from "../state.ts";

/** Markdown → DOM seguro: sem imagem de fora (rastreio), links em outra aba, títulos um nível abaixo (a página já tem h1). */
export function readerFragment(md: string, title: string | null): DocumentFragment {
  const html = marked.parse(md, { async: false, gfm: true }) as string;
  const frag = DOMPurify.sanitize(html, { RETURN_DOM_FRAGMENT: true, FORBID_TAGS: ["style", "form", "input", "button", "iframe", "video", "audio"] });
  for (const img of frag.querySelectorAll("img")) {
    if (MEDIA_SRC.test(img.getAttribute("src") ?? "")) img.loading = "lazy";
    else img.remove();
  }
  for (const a of frag.querySelectorAll("a[href]")) Object.assign(a, { target: "_blank", rel: "noopener noreferrer" });
  const first = frag.querySelector("h1");
  if (first && title && first.textContent?.trim().toLowerCase() === title.trim().toLowerCase()) first.remove();
  for (const h of frag.querySelectorAll("h1, h2, h3, h4, h5")) {
    const next = document.createElement(`h${Math.min(6, Number(h.tagName[1]) + 1)}`);
    next.append(...h.childNodes);
    h.replaceWith(next);
  }
  return frag;
}

export function Reader({ card: c, onClose }: { card: Card; onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const body = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<"loading" | "ok" | "error">("loading");
  const [minutes, setMinutes] = useState(0);
  useEffect(() => openOver(ref.current!), []);
  useEffect(() => {
    let on = true;
    store.reader(c.id).then(
      (md) => {
        if (!on) return;
        body.current!.replaceChildren(readerFragment(md, c.title));
        setMinutes(Math.max(1, Math.round((body.current!.textContent ?? "").split(/\s+/).length / 220)));
        setState("ok");
      },
      () => on && setState("error"),
    );
    return () => void (on = false);
  }, []);
  const src = c.sourceUrl && /^https?:\/\//i.test(c.sourceUrl) ? c.sourceUrl : null;
  const close = () => ref.current?.close();
  return (
    <dialog
      ref={ref}
      class="rd-reader"
      aria-label={`Leitura: ${c.title ?? c.sourceDomain ?? "artigo"}`}
      onCancel={(e) => {
        e.preventDefault();
        close();
      }}
      onClose={onClose}
    >
      <button type="button" class="rd-x rd-reader-x" aria-label="Sair da leitura" title="Sair da leitura (Esc)" onClick={close}>
        <IconClose />
      </button>
      <article class="rd-page">
        <header class="rd-page-head">
          <p class="rd-kicker">
            {c.sourceDomain?.replace(/^www\./, "")}
            {state === "ok" && <span> · {minutes} min de leitura</span>}
          </p>
          <h2 class="rd-page-title">{c.title ?? "Sem título"}</h2>
          {src && (
            <a class="open-site" href={src} target="_blank" rel="noopener noreferrer">
              Ver no site original
              <IconExternal />
              <span class="sr-only"> (abre em outra aba)</span>
            </a>
          )}
        </header>
        {state === "loading" && (
          <p class="rd-loading" role="status">
            <IconSpin />
            Abrindo a cópia de leitura…
          </p>
        )}
        {state === "error" && (
          <p class="rd-loading" role="status">
            Não deu para abrir a cópia de leitura agora.
          </p>
        )}
        <div class="rd-prose" ref={body} />
      </article>
    </dialog>
  );
}
